import * as Sentry from '@sentry/react';
import { formatPeriodLabel } from '../../../../../../components/general/DateRangeInput/utils/dateRangeUtils.ts';
import { useNotifications } from '../../../../../../lib/NotificationContext.tsx';
import {
    vatDeclarationApprovalRequestMessage,
    vatDeclarationApprovalWithdrawMessage,
    vatDeclarationSnapshotSaveMessage,
} from '../../../../../../lib/apiErrors.ts';
import type { VatDeclarationSnapshotSummary } from '../models.ts';
import type { VatReturnsState } from './useVatReturns.ts';

export interface VatReturnActions {
    saveDraft: () => Promise<void>;
    requestApproval: (version: VatDeclarationSnapshotSummary) => Promise<void>;
    withdrawApproval: (version: VatDeclarationSnapshotSummary, requestId: string) => Promise<void>;
}

export function useVatReturnActions(vatReturns: VatReturnsState): VatReturnActions {
    const { dispatch } = useNotifications();

    async function saveDraft(): Promise<void> {
        try {
            const saved = await vatReturns.saveDraft();
            dispatch({
                title: 'BTW-aangifte opgeslagen',
                description: `De BTW-aangifte voor ${formatPeriodLabel(saved.period_start, saved.period_end)} is opgeslagen als versie ${saved.version}.`,
                type: 'success',
            });
        } catch (error) {
            const expectedMessage = vatDeclarationSnapshotSaveMessage(error);
            if (expectedMessage === null) {
                Sentry.captureException(error);
            }
            dispatch({
                title: 'Fout bij opslaan BTW-aangifte',
                description: expectedMessage ?? 'De BTW-aangifte kon niet worden opgeslagen. Controleer de opgeslagen versies en probeer het zo nodig opnieuw.',
                type: 'error',
            });
        }
    }

    async function requestApproval(version: VatDeclarationSnapshotSummary): Promise<void> {
        try {
            await vatReturns.requestApproval(version.id);
            dispatch({
                title: 'Verstuurd ter goedkeuring',
                description: `De BTW-aangifte voor ${formatPeriodLabel(version.period_start, version.period_end)} wacht nu op goedkeuring van de klant.`,
                type: 'success',
            });
        } catch (error) {
            const expectedMessage = vatDeclarationApprovalRequestMessage(error);
            if (expectedMessage === null) {
                Sentry.captureException(error);
            }
            dispatch({
                title: 'Fout bij versturen ter goedkeuring',
                description: expectedMessage ?? 'De BTW-aangifte kon niet worden verstuurd. Controleer de status en probeer het zo nodig opnieuw.',
                type: 'error',
            });
        }
    }

    async function withdrawApproval(version: VatDeclarationSnapshotSummary, requestId: string): Promise<void> {
        try {
            await vatReturns.withdrawApproval(requestId);
            dispatch({
                title: 'Verzoek ingetrokken',
                description: `Het verzoek voor ${formatPeriodLabel(version.period_start, version.period_end)} is ingetrokken. Je kunt de versie opnieuw versturen of een nieuwe versie opslaan.`,
                type: 'success',
            });
        } catch (error) {
            const expectedMessage = vatDeclarationApprovalWithdrawMessage(error);
            if (expectedMessage === null) {
                Sentry.captureException(error);
            }
            dispatch({
                title: 'Fout bij intrekken',
                description: expectedMessage ?? 'Het verzoek kon niet worden ingetrokken. Controleer de status en probeer het zo nodig opnieuw.',
                type: 'error',
            });
        }
    }

    return { saveDraft, requestApproval, withdrawApproval };
}
